import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileDown, FileSpreadsheet } from 'lucide-react';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { Button } from '@/components/shared/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { useClassEvaluation } from '@/data/queries/useEvaluation';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { getScenarioBasicInfo } from '@/services/scenarioService';
import { downloadClassEvaluationExcel, downloadClassEvaluationRecordsExcel } from '@/services/evaluationService';
import { generateEvaluationResultsPdf } from '@/lib/generateEvaluationResultsPdf';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';

/**
 * 밀리초를 시간 형식으로 변환 (예: 3665000ms → "1h 1m 5s")
 */
function formatPlayTime(milliseconds: number | null | undefined): string {
    if (milliseconds == null || milliseconds === 0) return '0h 0m 0s';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    return `${hours}h ${minutes}m ${secs}s`;
}

export default function EvaluationResults(): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : '/admin';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = searchParams.get('tab') || 'evaluation';

    const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
    const [scenarioContentCode, setScenarioContentCode] = useState<string | null>(null);
    const [scenarioModeValue, setScenarioModeValue] = useState<string | null>(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [isDownloadingAllResults, setIsDownloadingAllResults] = useState(false);

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 시나리오 목록 추출 (curriculum에서 type === 'scenario'만)
    const scenarioList = useMemo(() => {
        if (!classItem?.curriculum) return [];
        return classItem.curriculum.filter((item) => item.type === 'scenario');
    }, [classItem]);

    // 첫 번째 시나리오를 기본 선택
    React.useEffect(() => {
        if (scenarioList.length > 0 && selectedScenarioId === null) {
            setSelectedScenarioId(scenarioList[0].id);
        }
    }, [scenarioList, selectedScenarioId]);

    // 선택된 시나리오 정보
    const selectedScenario = useMemo(() => {
        return scenarioList.find(s => s.id === selectedScenarioId) || null;
    }, [scenarioList, selectedScenarioId]);

    // 시나리오 선택 시 상세 정보 조회하여 contentCode(key)와 mode(modeValue) 가져오기
    React.useEffect(() => {
        const scenarioId = selectedScenario?.scenarioId;
        if (!scenarioId) {
            setScenarioContentCode(null);
            setScenarioModeValue(null);
            return;
        }

        const fetchScenarioKey = async () => {
            try {
                const scenarioInfo = await getScenarioBasicInfo(
                    scenarioId,
                    'ko',
                    String(classId)
                );
                // docs/api/scenario.md에서 확인
                const key = (scenarioInfo as any)?.key || null;
                const modeValue = (scenarioInfo as any)?.modeValue || null;
                console.log('🔍 [EvaluationResults] Scenario Info:', {
                    scenarioId,
                    key,
                    modeValue,
                    scenarioName: selectedScenario.name,
                });
                setScenarioContentCode(key);
                setScenarioModeValue(modeValue);
            } catch (error) {
                console.error('Failed to fetch scenario info:', error);
                setScenarioContentCode(null);
                setScenarioModeValue(null);
            }
        };

        fetchScenarioKey();
    }, [selectedScenario, classId]);

    // 평가결과 API 호출
    const {
        data: evaluationData,
        isError: isEvaluationError,
        isLoading: isEvaluationLoading,
    } = useClassEvaluation(
        classId,
        {
            contentCode: scenarioContentCode || undefined,
            mode: scenarioModeValue || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
        },
        {
            enabled: classId > 0 && selectedScenarioId !== null && scenarioContentCode !== null,
        }
    );

    const stats = evaluationData?.stats;
    const users = evaluationData?.userList || [];

    // 선택된 시나리오 이름
    const selectedScenarioName = selectedScenario?.name || t('evaluation.allScenarios');

    const backToClassDetailPath = (): string => {
        return `${basePath}/class-management/${classId}`;
    };

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            // 커리큘럼 탭으로 이동
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${basePath}/class-management/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            // 이미 평가결과이므로 아무것도 안 함
            return;
        } else if (tab === 'practice-status') {
            // 실습현황 탭으로 이동
            navigate(`${basePath}/class-management/${classId}/practice-status`);
        } else if (tab === 'statistics') {
            // 실습통계 탭으로 이동
            navigate(`${basePath}/class-management/${classId}/statistics`);
        } else if (tab === 'ranking') {
            // 랭킹 탭으로 이동
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            // 다른 탭으로 이동
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const handleExcelDownload = async () => {
        if (isDownloadingExcel) return;

        try {
            setIsDownloadingExcel(true);
            
            // 다운로드 날짜 (오늘 날짜) 생성: YYYY-MM-DD
            const today = new Date();
            const downloadDate = today.toISOString().split('T')[0];
            
            // 파일명 생성: 평가결과_클래스명_시나리오명_다운로드날짜.xlsx
            const filename = `평가결과_${classItem?.title || ''}_${selectedScenarioName}_${downloadDate}.xlsx`;

            await downloadClassEvaluationExcel(
                classId,
                {
                    contentCode: scenarioContentCode || undefined,
                    mode: scenarioModeValue || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                },
                filename,
                i18n.language
            );
        } catch (error) {
            console.error(t('evaluation.excelDownloadError'), error);
            alert(t('evaluation.excelDownloadFailed'));
        } finally {
            setIsDownloadingExcel(false);
        }
    };

    const handleAllResultsDownload = async () => {
        if (isDownloadingAllResults) return;

        try {
            setIsDownloadingAllResults(true);

            // 다운로드 날짜 (오늘 날짜) 생성: YYYY-MM-DD
            const today = new Date();
            const downloadDate = today.toISOString().split('T')[0];

            // 파일명 생성: 평가결과_전체_클래스명_다운로드날짜.xlsx
            const filename = `평가결과_전체_${classItem?.title || ''}_${downloadDate}.xlsx`;

            await downloadClassEvaluationRecordsExcel(classId, filename, i18n.language);
        } catch (error) {
            console.error(t('evaluation.excelDownloadError'), error);
            alert(t('evaluation.excelDownloadFailed'));
        } finally {
            setIsDownloadingAllResults(false);
        }
    };

    const handlePdfDownload = async () => {
        if (isDownloadingPdf || !stats || !classItem) return;

        try {
            setIsDownloadingPdf(true);
            
            // 조회기간: 시작일 또는 종료일 중 하나만 있어도 표시
            let dateRange: string | undefined;
            if (startDate && endDate) {
                dateRange = `${startDate} ~ ${endDate}`;
            } else if (startDate) {
                dateRange = `${startDate} ~`;
            } else if (endDate) {
                dateRange = `~ ${endDate}`;
            }

            await generateEvaluationResultsPdf({
                className: classItem.title,
                scenarioName: selectedScenarioName,
                dateRange,
                stats,
                users,
            });
        } catch (error) {
            console.error('PDF download error:', error);
            alert(t('evaluation.pdfDownloadFailed'));
        } finally {
            setIsDownloadingPdf(false);
        }
    };

    const tabs = [
        { id: 'curriculum', label: t('session.tabCurriculum') },
        { id: 'evaluation', label: t('session.tabEvaluation') },
        { id: 'practice-status', label: t('session.tabPracticeStatus') },
        { id: 'ranking', label: t('session.tabRanking') },
        { id: 'statistics', label: t('session.tabStatistics') },
    ];

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">{t('common.dateRange')}</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder={t('common.datePlaceholder')}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
            </div>
        );
    };


    if (isClassError || isClassLoading || !classItem) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem?.title || t('evaluation.title')}
                        onBack={() => navigate(backToClassDetailPath())}
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">
                    {isClassLoading ? t('evaluation.loadingData') : t('evaluation.loadError')}
                </div>
            </FullScreenLayout>
        );
    }

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex flex-1 min-h-0">
                {/* 좌측 사이드바 */}
                <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
                    {/* 헤더 (고정) */}
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
                        <h2 className="text-lg font-bold text-gray-900 truncate">
                            {t('evaluation.title')} <span className="text-sm font-normal text-gray-500">({scenarioList.length})</span>
                        </h2>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 shrink-0 px-2 text-xs whitespace-nowrap"
                            onClick={handleAllResultsDownload}
                            disabled={isDownloadingAllResults}
                        >
                            <FileSpreadsheet size={14} className="text-blue-500" />
                            {isDownloadingAllResults ? t('evaluation.downloading') : t('evaluation.downloadAllResults')}
                        </Button>
                    </div>
                    {/* 시나리오 리스트 (스크롤) */}
                    <div className="flex-1 overflow-y-auto p-4">
                        <div className="space-y-1">
                            {scenarioList.length === 0 ? (
                                <div className="text-sm text-gray-500 text-center py-4">
                                    {t('evaluation.noScenarios')}
                                </div>
                            ) : (
                                scenarioList.map((scenario) => (
                                    <button
                                        key={scenario.id}
                                        onClick={() => setSelectedScenarioId(scenario.id)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                            selectedScenarioId === scenario.id
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="font-medium">{scenario.name}</div>
                                        {scenario.practiceMode && (
                                            <div className="text-xs text-gray-500 mt-0.5">{scenario.practiceMode}</div>
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* 메인 영역 */}
                <div className="flex-1 bg-white overflow-y-auto">
                    <div className="p-8">
                        {/* 제목 + 다운로드 버튼 */}
                        <div className="flex items-center justify-between mb-6">
                            <h1 className="text-2xl font-bold text-gray-900">{selectedScenarioName}</h1>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    onClick={handleExcelDownload}
                                    disabled={isDownloadingExcel}
                                >
                                    <FileSpreadsheet size={16} className="text-blue-500" />
                                    {isDownloadingExcel ? t('evaluation.downloading') : t('evaluation.excelDownload')}
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-2"
                                    onClick={handlePdfDownload}
                                    disabled={isDownloadingPdf}
                                >
                                    <FileDown size={16} className="text-blue-500" />
                                    {isDownloadingPdf ? t('evaluation.downloading') : t('evaluation.pdfDownload')}
                                </Button>
                            </div>
                        </div>

                        {isEvaluationLoading ? (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                                {t('evaluation.loadingData')}
                            </div>
                        ) : isEvaluationError || !stats ? (
                            <div className="flex items-center justify-center h-64 text-gray-500">
                                {t('evaluation.loadingEvalData')}
                            </div>
                        ) : (
                            <>
                                {/* 통계 카드 */}
                                <div className="grid grid-cols-4 gap-4 mb-8">
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <div className="text-sm text-gray-600 mb-2">{t('evaluation.totalParticipantCount')}</div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {stats.total_participants}
                                            <span className="text-base font-normal text-gray-600 ml-1">{t('units.people')}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <div className="text-sm text-gray-600 mb-2">{t('evaluation.completionRate')}</div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {stats.completion_rate}
                                            <span className="text-base font-normal text-gray-600 ml-1">%</span>
                                        </div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <div className="text-sm text-gray-600 mb-2">{t('evaluation.totalPracticeCount')}</div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {stats.total_play_count}
                                            <span className="text-base font-normal text-gray-600 ml-1">{t('units.times')}</span>
                                        </div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                                        <div className="text-sm text-gray-600 mb-2">{t('evaluation.totalPracticeTime')}</div>
                                        <div className="text-3xl font-bold text-gray-900">
                                            {formatPlayTime(stats.total_play_time)}
                                        </div>
                                    </div>
                                </div>

                                {/* 테이블 */}
                                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                                    {t('evaluation.learner')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.practiceCount')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.practiceTime')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.latestScore')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.highestScore')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.lowestScore')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                                    {t('evaluation.status')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.length === 0 ? (
                                                <tr>
                                                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                                                        {t('evaluation.noEvaluationData')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                users.map((user, index) => {
                                                    const handleUserClick = () => {
                                                        if (!selectedScenario?.scenarioId) {
                                                            console.error('❌ scenarioId가 없습니다');
                                                            return;
                                                        }
                                                        
                                                        const path = `${basePath}/class-management/${classId}/results/${selectedScenario.scenarioId}/user/${user.user_id}`;
                                                        console.log('🖱️ User clicked:', user.user_id, user.user_name);
                                                        console.log('📍 Navigating to:', path);
                                                        console.log('📦 State:', { modeValue: scenarioModeValue || selectedScenario.modeValue });
                                                        
                                                        navigate(path, {
                                                            state: {
                                                                modeValue: scenarioModeValue || selectedScenario.modeValue
                                                            }
                                                        });
                                                    };

                                                    return (
                                                        <tr
                                                            key={user.user_id}
                                                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                                            onClick={handleUserClick}
                                                        >
                                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                                {user.user_name} ({user.user_email})
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                                {user.play_count}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                                {formatPlayTime(user.total_play_time)}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                                {user.latest_score ?? '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                                {user.highest_score ?? '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                                {user.lowest_score ?? '—'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm text-center">
                                                                <span className="text-gray-600">{user.status || '—'}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    );
}
